import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MessageSquare, Phone, Mail, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function AdminConsultations() {
  const { user } = useAuth();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (user) {
      fetchConsultations();
    }
  }, [user]);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${backendUrl}/api/admin/consultations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConsultations(response.data);
    } catch (error) {
      console.error('Error fetching consultations:', error);
      toast.error('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (consultationId, newStatus) => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      await axios.patch(
        `${backendUrl}/api/admin/consultations/${consultationId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Status updated successfully');
      fetchConsultations();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const saveNotes = async (consultationId) => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      await axios.patch(
        `${backendUrl}/api/admin/consultations/${consultationId}`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Notes saved successfully');
      setNotes('');
      setSelectedConsultation(null);
      fetchConsultations();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      PENDING: { label: 'Pending', className: 'bg-yellow-500' },
      CONTACTED: { label: 'Contacted', className: 'bg-blue-500' },
      SCHEDULED: { label: 'Scheduled', className: 'bg-purple-500' },
      COMPLETED: { label: 'Completed', className: 'bg-green-500' },
      CANCELLED: { label: 'Cancelled', className: 'bg-red-500' },
    };
    return config[status] || config.PENDING;
  };

  if (!user) return <div>Loading...</div>;

  return (
    <DashboardLayout role={user.role} title="Consultation Requests" user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Consultation Requests</h2>
            <p className="text-muted-foreground">Manage client consultation inquiries</p>
          </div>
          <Badge className="text-lg px-4 py-2">{consultations.length} Total</Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : consultations.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No consultation requests yet</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {consultations.map((consultation) => {
              const statusBadge = getStatusBadge(consultation.status);
              
              return (
                <Card key={consultation.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        {consultation.name}
                      </CardTitle>
                      <Badge className={`${statusBadge.className} text-white`}>
                        {statusBadge.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{consultation.email || 'N/A'}</span>
                      </div>
                      {consultation.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{consultation.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {new Date(consultation.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground">Service Interested In:</p>
                      <p className="font-medium">{consultation.service}</p>
                    </div>

                    {consultation.message && (
                      <div>
                        <p className="text-sm text-muted-foreground">Message:</p>
                        <p className="text-sm mt-1">{consultation.message}</p>
                      </div>
                    )}

                    {consultation.notes && (
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">Internal Notes:</p>
                        <p className="text-sm mt-1">{consultation.notes}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-3 pt-2 border-t">
                      <div className="flex-1">
                        <Label htmlFor={`status-${consultation.id}`} className="text-sm">
                          Update Status
                        </Label>
                        <Select
                          value={consultation.status}
                          onValueChange={(value) => updateStatus(consultation.id, value)}
                        >
                          <SelectTrigger id={`status-${consultation.id}`} className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PENDING">Pending</SelectItem>
                            <SelectItem value="CONTACTED">Contacted</SelectItem>
                            <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                            <SelectItem value="COMPLETED">Completed</SelectItem>
                            <SelectItem value="CANCELLED">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSelectedConsultation(consultation.id);
                          setNotes(consultation.notes || '');
                        }}
                      >
                        Add Notes
                      </Button>
                    </div>

                    {selectedConsultation === consultation.id && (
                      <div className="space-y-2 pt-2 border-t">
                        <Label htmlFor={`notes-${consultation.id}`}>Internal Notes</Label>
                        <Textarea
                          id={`notes-${consultation.id}`}
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Add internal notes about this consultation..."
                          rows={3}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => saveNotes(consultation.id)}>
                            Save Notes
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedConsultation(null);
                              setNotes('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
